import { forwardRef } from 'react';

import {
  Avatar,
  Checkbox,
  FormControl,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { FixedSizeList, type ListChildComponentProps } from 'react-window';

import { FilterSelectIcon } from '@modules/_shared/components/feed-v2/components/ActivityFilters/FilterSelectIcon';
import {
  checkboxStyles,
  getFilterSelectStyles,
  menuItemStyles,
} from '@modules/_shared/components/feed-v2/components/ActivityFilters/filterSelectStyles';
import { getInitialsFromDisplayName } from '@modules/_shared/utils/contact';
import { useLocale } from '@shared/hooks/locale';

export type ActivityParticipant = {
  id: string;
  name: string;
  imageUrl: string | null;
};

type ActivityParticipantFilterProps = {
  selectedParticipants: string[];
  onFilter: (selectedParticipants: string[]) => void;
  uniqueParticipants: ActivityParticipant[];
};

const ITEM_HEIGHT = 40;
const MAX_VISIBLE_ITEMS = 8;
const LIST_WIDTH = 280;

type ItemData = {
  participants: ActivityParticipant[];
  selectedParticipants: string[];
  onToggle: (id: string) => void;
};

const ParticipantRow = ({
  index,
  style,
  data,
}: ListChildComponentProps<ItemData>) => {
  const { participants, selectedParticipants, onToggle } = data;
  const participant = participants[index];

  return (
    <Stack
      direction="row"
      style={style}
      onClick={() => onToggle(participant.id)}
      sx={{
        alignItems: 'center',
        ...menuItemStyles,
        px: 2,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Checkbox
        checked={selectedParticipants.includes(participant.id)}
        size="small"
        sx={checkboxStyles}
        tabIndex={-1}
      />
      <Avatar
        src={participant.imageUrl ?? undefined}
        sx={{
          width: 20,
          height: 20,
          fontSize: '0.65rem',
          bgcolor: 'grey.400',
          mr: 1,
        }}
      >
        {getInitialsFromDisplayName(participant.name)}
      </Avatar>
      <Typography variant="body2" noWrap>
        {participant.name}
      </Typography>
    </Stack>
  );
};

const VirtualizedMenuList = forwardRef<
  HTMLDivElement,
  { children: React.ReactNode }
>(function VirtualizedMenuList(props, ref) {
  return <div ref={ref} {...props} />;
});

export const ActivityParticipantFilter = ({
  selectedParticipants,
  onFilter,
  uniqueParticipants,
}: ActivityParticipantFilterProps) => {
  const { t } = useLocale();

  const listHeight = Math.min(
    uniqueParticipants.length * ITEM_HEIGHT,
    MAX_VISIBLE_ITEMS * ITEM_HEIGHT,
  );

  const handleToggle = (id: string) => {
    if (selectedParticipants.includes(id)) {
      onFilter(selectedParticipants.filter(p => p !== id));
    } else {
      onFilter([...selectedParticipants, id]);
    }
  };

  const itemData: ItemData = {
    participants: uniqueParticipants,
    selectedParticipants,
    onToggle: handleToggle,
  };

  return (
    <FormControl
      size="small"
      sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}
    >
      <Select
        multiple
        displayEmpty
        value={selectedParticipants}
        onChange={e => onFilter(e.target.value as string[])}
        IconComponent={FilterSelectIcon}
        input={<OutlinedInput />}
        renderValue={selected => {
          if (selected.length === 0) {
            return t('All participants');
          }

          const firstParticipant = uniqueParticipants.find(
            p => p.id === selected[0],
          );

          const firstName = firstParticipant?.name ?? t('Unknown');

          if (selected.length === 1) {
            return firstName;
          }

          return `${firstName} +${selected.length - 1}`;
        }}
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                mt: 0.5,
                maxHeight: 'none',
                minWidth: LIST_WIDTH,
              },
            },
            list: {
              component: VirtualizedMenuList,
              sx: { p: 0 },
            },
          },
        }}
        sx={getFilterSelectStyles(selectedParticipants.length > 0)}
      >
        <FixedSizeList<ItemData>
          width={LIST_WIDTH}
          height={listHeight}
          itemSize={ITEM_HEIGHT}
          itemCount={uniqueParticipants.length}
          itemData={itemData}
          overscanCount={5}
        >
          {ParticipantRow}
        </FixedSizeList>
      </Select>
    </FormControl>
  );
};
